/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { AnalyticsContext } from '@atlaskit/analytics-next';
import { IconButton, LinkButton } from '@atlaskit/button/new';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { cssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import Image from '@atlaskit/image';
import { Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { AdControls } from '@post-office/components--ad-controls';
import { type NextBestAction } from '@post-office/confluence-next-best-action';
import { useMessageInstanceEvent } from '@post-office/message-instance-event-frontend-client';
import { useMessageEvent } from '@post-office/message-lifecycle';
import {
	type BackendPlacementMessage,
	defaultMessageBackend,
	type FrontendPlacementMessage,
} from '@post-office/placement-common-v2';
import {
	PlacementBackend,
	PlacementFrontend,
} from '@post-office/placements--growth/confluence-side-nav';
import { FormattedMessage, useIntl } from 'react-intl-next';

import NotesImage from './images/notes.png';
import { type MessageTemplateContext } from '../../..';
import { assertMessageTemplateContext } from '../../../__generated__/validation/messageTemplateContext';
import {
	type CreatePlacementMessageConfig,
	type BackendSyncMessageStage,
	type AnyPayload,
	type AnyBackendMessageStage,
	type AnyFrontendStage,
} from '@post-office/placement-common-v2/create-placement/types';
import { type TransformMetadataResult } from '@post-office/shared-contracts/message-template-metadata';

export const MessageBackend: (config: CreatePlacementMessageConfig) => BackendPlacementMessage<{
	transformMetadata: BackendSyncMessageStage<AnyPayload, TransformMetadataResult>;
	hydrate?: AnyBackendMessageStage;
}> = PlacementBackend.createMessageBackend(defaultMessageBackend);

const whyAmISeeingThisModalContent = {
	title: (
		<FormattedMessage
			id="post-office.confluence-side-nav.next-best-action.1-on-1-loom-nudge.ad-controls.why-am-i-seeing-this"
			description="the title to the modal"
			defaultMessage="Why am I seeing this?"
		/>
	),
	message: (
		<p>
			<FormattedMessage
				id="post-office.confluence-side-nav.next-best-action.1-on-1-loom-nudge.ad-controls.recommendation-basis"
				description="the main message of the modal"
				defaultMessage="This recommendation is based on:"
			/>
		</p>
	),
	reasons: [
		<FormattedMessage
			id="post-office.confluence-side-nav.next-best-action.1-on-1-loom-nudge.ad-controls.actions-done-so-far"
			description="the first reason for displaying the message to the customer: what actions the customer has actioned so far in the product"
			defaultMessage="What you’ve done so far in the product"
		/>,
		<FormattedMessage
			id="post-office.confluence-side-nav.next-best-action.1-on-1-loom-nudge.ad-controls.suggestions"
			description="the second reason for displaying the message to the customer: what we suggest to help the customer"
			defaultMessage="What we suggest to help you get the most value from Atlassian"
		/>,
	],
};

const styles = cssMap({
	container: {
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.large'),
		padding: token('space.150'),
		width: '100%',
	},
	body: {
		marginTop: token('space.100'),
	},
	ctaWrapper: {
		marginTop: token('space.150'),
	},
});

type MessageFrontendProps = MessageTemplateContext & {
	onSideNavClick: (nextBestActionType: NextBestAction) => string | void | null;
	handleOnDismissed: () => void;
};

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: AnyFrontendStage;
}> = PlacementFrontend.createMessageFrontend({
	render: {
		validate: assertMessageTemplateContext,

		Component: ({
			nextBestActionType,
			handleOnDismissed,
			onSideNavClick,
		}: MessageFrontendProps) => {
			const { messageInstanceClicked } = useMessageInstanceEvent();
			const { messageDismissed } = useMessageEvent();

			const intl = useIntl();
			const headerText = intl.formatMessage({
				id: 'post-office.confluence-side-nav.next-best-action.1-on-1-loom-nudge.header',
				defaultMessage: 'Automate meeting notes',
				description: 'Header text for confluence side nav next best action banner',
			});

			const bodyText = intl.formatMessage({
				id: 'post-office.confluence-side-nav.next-best-action.1-on-1-loom-nudge.body',
				defaultMessage:
					'Instead of manually typing meeting notes, why not try Loom to record meetings?',
				description: 'Body for confluence side nav next best action banner',
			});

			const ctaText = intl.formatMessage({
				id: 'post-office.confluence-side-nav.next-best-action.1-on-1-loom-nudge.cta',
				defaultMessage: 'Try now',
				description: 'Call to action text for confluence side nav next best action banner',
			});

			const closeLabel = intl.formatMessage({
				id: 'post-office.confluence-side-nav.next-best-action.1-on-1-loom-nudge.close',
				defaultMessage: 'Close',
				description: 'Label for the close icon that is represented by an X',
			});

			const imageAltText = intl.formatMessage({
				id: 'post-office.confluence-side-nav.next-best-action.1-on-1-loom-nudge.image-alt',
				defaultMessage: 'Notepad with sparkling background',
				description: 'Alternate text for picture of notepad with sparkles behind it',
			});

			const handleDismiss = () => {
				void messageInstanceClicked({
					messageEventType: 'clicked',
					clickedElement: {
						clickIdentifier: 'dismiss',
						clickedElement: 'button',
						buttonType: 'secondary',
					},
					action: {
						actionType: 'dismiss',
					},
					additionalMetadataFromFrontend: {},
				});
				void messageDismissed();
				void handleOnDismissed();
			};

			const handleCloseClicked = () => {
				void messageInstanceClicked({
					messageEventType: 'clicked',
					clickedElement: {
						clickIdentifier: '1on1-loom-close',
						clickedElement: 'button',
						buttonType: 'secondary',
					},
					action: {
						actionType: 'dismiss',
					},
					additionalMetadataFromFrontend: {},
				});
				void messageDismissed();
				void onSideNavClick(nextBestActionType);
			};

			const handleCtaClicked = () => {
				void messageInstanceClicked({
					messageEventType: 'clicked',
					clickedElement: {
						clickIdentifier: '1on1-loom-cta',
						clickedElement: 'button',
						buttonType: 'primary',
					},
					action: {
						actionType: 'open',
					},
					additionalMetadataFromFrontend: {},
				});
				void messageDismissed();
				void onSideNavClick(nextBestActionType);
			};

			return (
				<AnalyticsContext data={{ source: 'confluence-side-nav-1-on-1-loom-nudge-message' }}>
					<div css={styles.container}>
						<Inline spread="space-between">
							<Image src={NotesImage} alt={imageAltText} height="56" width="56" />
							<Inline>
								<AdControls
									iconSize="small"
									showWhyAmISeeingThis={true}
									whyAmISeeingThisModalContent={whyAmISeeingThisModalContent}
									onDismissed={handleDismiss}
								/>
								<IconButton
									onClick={handleCloseClicked}
									appearance="subtle"
									icon={CrossIcon}
									label={closeLabel}
								/>
							</Inline>
						</Inline>
						<Heading size="small">{headerText}</Heading>
						<div css={styles.body}>{bodyText}</div>
						<div css={styles.ctaWrapper}>
							<LinkButton
								onClick={handleCtaClicked}
								href="https://www.loom.com/welcome"
								spacing="compact"
								target="_blank"
							>
								{ctaText}
							</LinkButton>
						</div>
					</div>
				</AnalyticsContext>
			);
		},
	},
});
